/**
 * PeekBottomSheet — Mobile peek panel variant (slides up from bottom).
 * Desktop continues to use PeekContainer (slides from right).
 * Swipe down to dismiss; swipe up to expand.
 */
import React, { useRef, useState, useCallback, useEffect } from 'react';
import './PeekBottomSheet.css';

export interface PeekBottomSheetProps {
  id: string;
  title: string;
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
  /** Height when snapped to peek state (default 60vh) */
  peekHeight?: string;
  /** Height when expanded (default 95vh) */
  expandedHeight?: string;
  className?: string;
}

type SheetState = 'closed' | 'peek' | 'expanded';

export const PeekBottomSheet: React.FC<PeekBottomSheetProps> = ({
  id,
  title,
  children,
  isOpen,
  onClose,
  peekHeight = '60vh',
  expandedHeight = '95vh',
  className = '',
}) => {
  const [sheetState, setSheetState] = useState<SheetState>('closed');
  const dragRef = useRef<{ startY: number; startSheetState: SheetState } | null>(null);

  // Sync open → peek on mount
  useEffect(() => {
    if (isOpen && sheetState === 'closed') {
      setSheetState('peek');
    }
    if (!isOpen) {
      setSheetState('closed');
    }
  }, [isOpen]);

  const snapTo = useCallback((state: SheetState) => {
    setSheetState(state);
  }, []);

  const handleDragStart = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    e.preventDefault();
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    dragRef.current = { startY: clientY, startSheetState: sheetState };
  }, [sheetState]);

  const handleDragMove = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    if (!dragRef.current) return;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const delta = clientY - dragRef.current.startY;

    // Visual feedback via CSS transform while dragging — actual snap on release
    const sheet = document.getElementById(`peek-sheet-${id}`);
    if (!sheet) return;

    if (delta > 0) {
      // Swiping down — peek moves down
      sheet.style.transform = `translateY(${Math.min(delta, 200)}px)`;
      sheet.style.transition = 'none';
    } else {
      // Swiping up — peek moves up
      sheet.style.transform = `translateY(${Math.max(delta, -200)}px)`;
      sheet.style.transition = 'none';
    }
  }, [id]);

  const handleDragEnd = useCallback(() => {
    if (!dragRef.current) return;
    const sheet = document.getElementById(`peek-sheet-${id}`);
    if (sheet) {
      sheet.style.transform = '';
      sheet.style.transition = '';
    }
    // Delta > 80px → close; delta < -80px → expand
    // We don't track actual delta here cleanly — use last direction from dragRef
    dragRef.current = null;
  }, [id]);

  // Swipe detection via pointer events (unified touch+mouse)
  const onPointerDown = useCallback((e: React.PointerEvent) => {
    dragRef.current = { startY: e.clientY, startSheetState: sheetState };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [sheetState]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const delta = e.clientY - dragRef.current.startY;
    const sheet = document.getElementById(`peek-sheet-${id}`);
    if (!sheet) return;
    sheet.style.transform = delta > 0
      ? `translateY(${Math.min(delta, 300)}px)`
      : `translateY(${Math.max(delta, -300)}px)`;
    sheet.style.transition = 'none';
  }, [id]);

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const delta = e.clientY - dragRef.current.startY;
    const threshold = 60;

    if (delta > threshold) {
      // Swiped down → close
      setSheetState('closed');
      onClose();
    } else if (delta < -threshold && dragRef.current.startSheetState === 'peek') {
      // Swiped up from peek → expand
      setSheetState('expanded');
    }
    dragRef.current = null;
  }, [onClose]);

  const heightStyle = sheetState === 'expanded' ? expandedHeight
    : sheetState === 'peek' ? peekHeight
    : '0px';

  const translateClass = sheetState === 'closed' ? 'sheet-closed' : 'sheet-open';

  return (
    <>
      {/* Backdrop — only when actually open AND not fully closed */}
      {isOpen && sheetState !== 'closed' && (
        <div
          className="peek-sheet-backdrop"
          onClick={() => { setSheetState('closed'); onClose(); }}
          aria-hidden="true"
        />
      )}

      {/* Sheet */}
      <div
        id={`peek-sheet-${id}`}
        className={`peek-bottom-sheet ${translateClass} ${className}`}
        style={{ height: heightStyle }}
        data-testid={`peek-sheet-${id}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        {/* Drag affordance */}
        <div
          className="peek-sheet-handle"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <div className="peek-sheet-handle-bar" />
          <div className="peek-sheet-header">
            <h2 className="peek-sheet-title">{title}</h2>
            <button
              className="peek-sheet-close"
              onClick={() => { setSheetState('closed'); onClose(); }}
              aria-label="Close"
            >×</button>
          </div>
        </div>

        {/* Content */}
        <div className="peek-sheet-content">
          {children}
        </div>
      </div>
    </>
  );
};

export default PeekBottomSheet;